import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signAsset } from '@/api/assets';
import { uploadSignedAsset } from '@/api/misc';
import { patchProjectTemplateTdm } from '@/api/Sherpa/ProjectTemplate';
import { notifyUser } from '@/store/Toast';
import type { TDMSlugType } from '@/types/Tdm';
import { sanitizeResponseError } from '@/utils';

type UploadTemplateFunction = (data: { templateFile: File[]; tdmId: number; templateId: number }) => Promise<void>;
interface IUseProjectTemplateFileUpload {
  uploadTemplate: UploadTemplateFunction;
  isLoading: boolean;
  uploadSuccess: boolean;
  isError: boolean;
}

export const useProjectTemplateFileUpload = (tdmType: TDMSlugType): IUseProjectTemplateFileUpload => {
  const tdmTypeRef = useRef<TDMSlugType | undefined>(undefined);
  const tdmIdRef = useRef<number | undefined>(undefined);
  const templateIdRef = useRef<number | undefined>(undefined);
  const templateFileRef = useRef<File[] | undefined>(undefined);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    tdmTypeRef.current = tdmType;
  }, [tdmType]);

  const uploadAsset = useMutation(
    ({ sign, files }: { sign: any[]; files: File[] }) =>
      Promise.allSettled(sign.map(({ value }, index) => uploadSignedAsset({ file: files[index], url: value.url }))),
    {
      onSuccess: (_, { sign, files }) => {
        const assetsFiles = files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: sign[index].value.fKey,
            contentType: file.type,
            size: file.size,
          };
        });
        updateProjectTemplateTdm.mutate({
          data: {
            resourceType: tdmTypeRef.current!,
            templateTdm: { templateFile: assetsFiles },
          },
          templateId: templateIdRef.current!,
          tdmId: tdmIdRef.current!,
        });
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload Template File. Try again.',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const signAssetFiles = useMutation(
    (payloads: {
      signAssetPayload: {
        key: string;
        type: string;
        resource?: string;
      }[];
    }) => Promise.allSettled(payloads.signAssetPayload.map((payload) => signAsset(payload))),
    {
      onSuccess: (data) => {
        uploadAsset.mutate({
          sign: data,
          files: templateFileRef.current!,
        });
      },
    }
  );

  const updateProjectTemplateTdm = useMutation(
    (payload: { data: any; templateId: number; tdmId: number }) => patchProjectTemplateTdm({ ...payload }),
    {
      onSuccess: () => {
        setUploadSuccess(true);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadTemplate = useCallback<UploadTemplateFunction>(
    async ({ templateFile, tdmId, templateId }) => {
      templateFileRef.current = templateFile;
      tdmIdRef.current = tdmId;
      templateIdRef.current = templateId;

      const signPayload = templateFile.map<{
        key: string;
        type: string;
        resource: string;
      }>((file) => ({
        key: file.name,
        type: file.type,
        resource: 'templates',
      }));

      const payload = {
        signAssetPayload: signPayload,
      };

      signAssetFiles.mutate(payload);
    },
    [signAssetFiles]
  );

  return {
    uploadTemplate,
    isLoading: signAssetFiles.isLoading || updateProjectTemplateTdm.isLoading || uploadAsset.isLoading,
    uploadSuccess: uploadSuccess,
    isError: signAssetFiles.isError || updateProjectTemplateTdm.isError || uploadAsset.isError,
  };
};
